// components/funnel/CustomersModal.jsx
import React from "react";
import {
  X, Users, Phone, UserCircle2, MessageSquareQuote, Clock,
  CheckCircle2, AlertCircle, Sparkles, ListChecks,
} from "lucide-react";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  Customer Card
 * ================================================================ */
const CustomerCard = ({ customer, index }) => {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";

  const hasName =
    customer.firstName !== "" && customer.firstName !== null && customer.firstName !== undefined;
  const fullName = hasName
    ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim()
    : null;

  return (
    <div
      className="
        group relative overflow-hidden
        rounded-2xl
        bg-white/70 dark:!bg-slate-900/70
        backdrop-blur-md
        border border-teal-500/15 dark:!border-teal-400/15
        transition-all duration-300
        hover:border-amber-300/50
        hover:shadow-[0_8px_24px_-12px_rgba(15,118,110,0.5)]
        hover:scale-[1.01]
      "
      style={{ animation: `itemIn 0.4s ease-out ${index * 0.04}s both` }}
    >
      {/* نقطه شمارنده بالا */}
      <span className={`absolute top-2.5 right-2.5 flex h-6 min-w-6 items-center justify-center rounded-lg px-1.5 text-[10px] font-extrabold tabular-nums bg-teal-500/15 text-teal-700 dark:!text-teal-300 border border-teal-500/30 ${isEnglish && 'font-sans'}`}>
        {index + 1}
      </span>

      <div className="relative p-4 pt-4">
        {/* ---------- Row 1: Phone + Name ---------- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">

          {/* Phone */}
          <div className="flex items-center gap-2.5">
            <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-amber-500/20 border border-amber-300/50">
              <Phone size={13} className="text-amber-700 dark:!text-amber-300" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-slate-500 dark:!text-slate-400">
                {t("customersModal.customerCard.phone")}
              </p>
              <p
                className="text-xs sm:text-sm font-extrabold text-slate-800 dark:!text-amber-100 tabular-nums font-mono"
                dir="ltr"
              >
                {customer.phone ? `0${customer.phone}` : "—"}
              </p>
            </div>
          </div>

          {/* Name */}
          <div className="flex items-center gap-2.5">
            <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-teal-400/40 to-teal-600/20 border border-teal-400/50">
              <UserCircle2 size={13} className="text-teal-700 dark:!text-teal-300" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-slate-500 dark:!text-slate-400">
                {t("customersModal.customerCard.name")}
              </p>
              <p className="text-xs sm:text-sm font-extrabold text-slate-800 dark:!text-amber-100 truncate">
                {fullName || t("customersModal.customerCard.unknownName")}
              </p>
            </div>
          </div>
        </div>

        {/* ---------- Reason ---------- */}
        {customer.reason && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-gradient-to-l from-teal-500/5 to-amber-300/5 border border-teal-500/15 dark:!border-teal-400/10 p-3">
            <span className="shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/50">
              <MessageSquareQuote size={11} className="text-amber-600 dark:!text-amber-300" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-amber-700 dark:!text-amber-300 mb-0.5">
                {t("customersModal.customerCard.reason")}
              </p>
              <p className="text-[11px] sm:text-xs text-slate-600 dark:!text-slate-300 leading-6 break-words">
                {customer.reason}
              </p>
            </div>
          </div>
        )}

        {/* ---------- Analyzed date ---------- */}
        {customer.lastAnalyzedAt && (
          <div className="mt-2.5 flex items-center gap-1.5 text-[10px] text-slate-400 dark:!text-slate-500">
            <Clock size={10} />
            <span>{t("customersModal.customerCard.lastAnalyzed")}</span>
            <span className="font-bold tabular-nums">
              {new Date(customer.lastAnalyzedAt).toLocaleString("fa-IR")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ================================================================
 *  Main Modal
 * ================================================================ */
const CustomersModal = ({
  isOpen,
  onClose,
  stageName,
  customers,
  loading,
}) => {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";

  if (!isOpen) return null;

  const count = Array.isArray(customers) ? customers.length : 0;

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(24px) scale(.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes itemIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .modal-backdrop-anim { animation: fadeIn .25s ease-out; }
        .modal-panel-anim { animation: modalIn .4s cubic-bezier(.2,.9,.3,1.15); }
      `}</style>

      <div
        className="modal-backdrop-anim fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 dark:!bg-black/70 backdrop-blur-md p-3 sm:p-4"
        onClick={onClose}
      >
        <div
          className="
            modal-panel-anim relative
            w-full max-w-2xl max-h-[88vh] overflow-hidden
            rounded-3xl
            bg-white dark:!bg-slate-900
            border border-teal-500/20 dark:!border-teal-400/15
            shadow-[0_25px_70px_-20px_rgba(15,118,110,0.5)]
            dark:!shadow-[0_25px_70px_-20px_rgba(0,0,0,0.9)]
          "
          onClick={(e) => e.stopPropagation()}
        >
          {/* خط طلایی بالا */}
          <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-l from-transparent via-amber-400/90 to-transparent z-10" />

          {/* گلوه‌های تزئینی */}
          <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/15 dark:!bg-amber-400/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/15 dark:!bg-teal-500/10 blur-3xl" />

          {/* ============ Header ============ */}
          <div className="relative flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-teal-500/10 dark:!border-teal-400/10">
            <div className="flex items-center gap-3 min-w-0">
              <span className="shrink-0 relative flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 shadow-[0_8px_24px_-8px_rgba(251,191,36,0.8)] border border-amber-200/60 dark:!border-amber-400/30">
                <Users className="h-5 w-5 text-white drop-shadow" />
                <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-amber-100" />
              </span>

              <div className="min-w-0">
                <h3 className="text-sm sm:text-base font-extrabold bg-gradient-to-l from-teal-700 via-teal-600 to-amber-500 dark:!from-amber-200 dark:!via-teal-300 dark:!to-teal-400 bg-clip-text text-transparent truncate">
                  {stageName || t("customersModal.defaultTitle")}
                </h3>
                <p className="text-[10px] sm:text-[11px] text-slate-500 dark:!text-slate-400 mt-0.5 truncate">
                  {t("customersModal.subtitle")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Counter chip */}
              {!loading && count > 0 && (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-extrabold tabular-nums bg-teal-500/15 text-teal-700 dark:!text-teal-300 border border-teal-500/30 ${isEnglish && 'font-sans'}`}>
                  <ListChecks size={11} />
                  {count.toLocaleString(isEnglish ? "en-US" : "fa-IR")}
                </span>
              )}

              <button
                onClick={onClose}
                aria-label={t("customersModal.close")}
                className="
                  group h-9 w-9 flex items-center justify-center rounded-xl
                  text-slate-400 dark:!text-slate-500
                  bg-slate-100/60 hover:bg-rose-500/15 dark:!bg-white/5 dark:!hover:bg-rose-500/20
                  hover:text-rose-500 dark:!hover:text-rose-400
                  border border-slate-200/60 dark:!border-white/10 hover:border-rose-400/40
                  transition-all duration-300
                  hover:rotate-90 hover:scale-110 active:scale-95
                "
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* ============ Body ============ */}
          <div className="relative max-h-[calc(88vh-140px)] overflow-y-auto custom-scrollbar p-4 sm:p-5">

            {/* Loading */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-14 gap-3">
                <span className="h-9 w-9 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
                <p className="text-xs sm:text-sm text-slate-500 dark:!text-slate-400">
                  {t("customersModal.loading")}
                </p>
              </div>
            ) : count === 0 ? (
              /* Empty */
              <div className="flex flex-col items-center justify-center py-14 gap-3">
                <div className="relative">
                  <span
                    className="absolute inset-0 -m-2 rounded-full bg-amber-400/15 animate-ping"
                    style={{ animationDuration: "2s" }}
                  />
                  <div className="relative h-16 w-16 rounded-full bg-gradient-to-br from-amber-300/20 to-teal-500/15 border border-amber-300/40 dark:!border-amber-400/20 flex items-center justify-center">
                    <Users size={26} className="text-amber-500 dark:!text-amber-300" />
                  </div>
                </div>
                <p className="text-sm font-bold text-slate-600 dark:!text-slate-300">
                  {t("customersModal.empty")}
                </p>
                <p className="text-[11px] text-slate-400 dark:!text-slate-500">
                  {t("customersModal.emptyHint")}
                </p>
              </div>
            ) : (
              /* List */
              <div className="space-y-3">
                {customers.map((customer, index) => (
                  <CustomerCard
                    key={customer.userId || index}
                    customer={customer}
                    index={index}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ============ Footer ============ */}
          <div className="relative flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-teal-500/10 dark:!border-teal-400/10 bg-teal-500/5 dark:!bg-teal-400/5">
            <button
              onClick={onClose}
              className="
                group relative overflow-hidden
                flex-1 sm:flex-none sm:px-6
                flex items-center justify-center gap-2
                py-2.5 px-4 rounded-xl
                text-xs sm:text-sm font-bold text-white
                bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
                shadow-[0_6px_20px_-6px_rgba(15,118,110,0.8)]
                hover:from-teal-600 hover:via-teal-500 hover:to-teal-400
                hover:shadow-[0_8px_24px_-6px_rgba(20,184,166,1)]
                transition-all duration-300
                hover:scale-[1.02] active:scale-[0.98]
              "
            >
              <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/25 to-transparent" />
              <span className="relative">{t("customersModal.close")}</span>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(251,191,36,0.9));
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
      `}</style>
    </>
  );
};

export default CustomersModal;